import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSmile, faTimes, faPaperclip, faFile, faImage, faMicrophone, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import EmojiPicker from 'emoji-picker-react';
import Toast from '../../../components/Widgets/CustomToast';

const NewMessageModal = ({ 
  isOpen, 
  onClose, 
  onSend, 
  username, 
  password, 
  cloudId, 
  contacts, 
  areAllRecipientsInternal,
  dids, 
  setSelectedDid, 
  selectedDid,
  initialRecipient 
}) => {
  const [recipients, setRecipients] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [message, setMessage] = useState('');
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [allContacts, setAllContacts] = useState({});
  const [sending, setSending] = useState(false);
  const textareaRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [showDidDropdown, setShowDidDropdown] = useState(false);

  const isSingleRecipientOnly =
    cloudId?.replaceAll('*', '').toUpperCase() === 'INTERACTIVETELVIP';

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset form fields when modal opens
  useEffect(() => {
    if (isOpen) {
      // Only reset recipients if there's no initialRecipient
      if (!initialRecipient) {
        setRecipients([]);
      }
      setInputValue('');
      setMessage('');
      setIsMultiLine(false);
      setShowDropdown(false);
      setShowEmojiPicker(false);
      setFilePreview(null);
      setSelectedFile(null);
      setFileType(null);
      
      // Transform contacts object into array format
      const formattedContacts = Object.entries(contacts || {}).map(([key, contact], index) => ({
        id: index + 1,
        name: contact,
        number: key
      }));
      setFilteredContacts(formattedContacts);
    }
  }, [isOpen, contacts, initialRecipient]);

  useEffect(() => {
    if (dids?.length > 0) {
      setSelectedDid(dids[0] || {});
    }
  }, [dids, setSelectedDid]);

  // Handle initial recipient
  useEffect(() => {
    console.log("initialRecipient is ", initialRecipient);
    if (isOpen && initialRecipient && !recipients.includes(initialRecipient)) {
      setRecipients([initialRecipient]);
    }
  }, [isOpen, initialRecipient]);

  // Handle focusing input and scrolling on mobile
  useEffect(() => {
    const handleFocus = () => {
      // Small delay to ensure the keyboard is fully open
      setTimeout(() => {
        if (textareaRef.current) {
          // Scroll the textarea into view
          textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Additional scroll for iOS
          window.scrollTo({
            top: window.scrollY + (window.innerHeight * 0.3),
            behavior: 'smooth'
          });
        }
      }, 300);
    };

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('focus', handleFocus);
      return () => textarea.removeEventListener('focus', handleFocus);
    }
  }, [isOpen]); // Re-run when modal opens

  // Handle modal open
  useEffect(() => {
    if (isOpen) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
      // Force viewport to not scale or zoom on mobile
      const viewportMeta = document.querySelector('meta[name=viewport]');
      const originalContent = viewportMeta?.getAttribute('content');
      viewportMeta?.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');

      return () => {
        // Restore original body and viewport state
        document.body.style.overflow = '';
        if (originalContent) {
          viewportMeta?.setAttribute('content', originalContent);
        }
      };
    }
  }, [isOpen]);

  // Update modal positioning when keyboard opens/closes
  useEffect(() => {
    const handleResize = () => {
      // Check if it's likely that the keyboard is open
      const isKeyboardOpen = window.innerHeight < window.outerHeight * 0.8;
      
      if (isKeyboardOpen && textareaRef.current) {
        textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to determine if a value is an extension (contains @) or phone number
  const isExtension = (value) => {
    return value.includes('@');
  };

  // Helper function to check if recipients can be mixed
  const canAddRecipient = (newRecipient) => {
    if (recipients.length === 0) return true;
    
    const newIsExtension = isExtension(newRecipient);
    const existingIsExtension = isExtension(recipients[0]);
    
    return newIsExtension === existingIsExtension;
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 120);
      textarea.style.height = `${newHeight}px`;
      setIsMultiLine(newHeight > 48);
    }
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const cursorPosition = textareaRef.current.selectionStart;
    const updatedMessage = message.slice(0, cursorPosition) + emoji + message.slice(cursorPosition);
    setMessage(updatedMessage);
    setShowEmojiPicker(false);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setShowMediaOptions(false); // Hide media options immediately after file selection
    const fileReader = new FileReader();
    
    fileReader.onload = (e) => {
      const type = file.type.split('/')[0];
      setFileType(type);
      setFilePreview(e.target.result);
    };

    if (file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/')) {
      fileReader.readAsDataURL(file);
    } else {
      setFileType('application');
      setFilePreview(URL.createObjectURL(file));
    }
  };

  const clearFilePreview = () => {
    setFilePreview(null);
    setSelectedFile(null);
    setFileType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async () => {
    if (sending) {
      return;
    }
    
    if ((message.trim() || selectedFile) && recipients.length > 0) {
      try {
        setSending(true);
        await onSend(recipients, message, selectedFile);
        setMessage('');
        clearFilePreview();
        
        // Remove new_message and to_user from URL
        const url = new URL(window.location.href);
        url.searchParams.delete('new_message');
        url.searchParams.delete('to_user');
        window.history.replaceState({}, '', url.toString());
        
        onClose();
      } catch (error) {
        console.error('Error sending message:', error);
        // Keep the modal open and file preview in case of error
      } finally {
        setSending(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Filter contacts based on input
    if (value.trim()) {
      const filtered = Object.entries(contacts || {}).map(([key, contact], index) => ({
        id: index + 1,
        name: contact,
        number: key
      })).filter(contact => 
        contact.name?.toLowerCase().includes(value.toLowerCase()) || 
        contact.number?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredContacts(filtered);
    } else {
      // Show all contacts when input is empty
      const allContacts = Object.entries(contacts || {}).map(([key, contact], index) => ({
        id: index + 1,
        name: contact,
        number: key
      }));
      setFilteredContacts(allContacts);
    }
    setShowDropdown(true);
  };

  const handleInputFocus = () => {
    // Show all contacts when input is focused
    const allContacts = Object.entries(contacts || {}).map(([key, contact], index) => ({
      id: index + 1,
      name: contact,
      number: key
    }));
    setFilteredContacts(allContacts);
    setShowDropdown(true);
  };

  const handleInputKeyDown = (e) => {
    // Handle Tab key first to prevent default behavior
    if (e.key === 'Tab' && inputValue.trim()) {
      e.preventDefault(); // This needs to be called first for Tab
      addRecipient(inputValue);
      return; // Exit early after handling Tab
    }

    // Handle backspace to remove last recipient
    if (e.key === 'Backspace' && !inputValue && recipients.length > 0) {
      removeRecipient(recipients[recipients.length - 1]);
    }

    // Handle Enter or comma to add new recipient
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      addRecipient(inputValue);
    }

    // Handle space to add new recipient
    if (e.key === ' ' && inputValue.trim()) {
      e.preventDefault();
      addRecipient(inputValue);
    }
  };

  const addRecipient = (value) => {
    // Remove commas, spaces, parentheses, dashes, and plus signs
    const trimmedValue = value.trim()
      .replace(/,/g, '')
      .replace(/\s+/g, '')
      .replace(/[()\-+]/g, '');
    
    if (!trimmedValue) return;
    
    if (recipients.includes(trimmedValue)) return;

    if (isSingleRecipientOnly && recipients.length >= 1) {
      Toast.error('Only one recipient is allowed.');
      setInputValue('');
      setShowDropdown(false);
      return;
    }
    
    // Check if we can add this recipient type
    if (!canAddRecipient(trimmedValue)) {
      const existingType = isExtension(recipients[0]) ? 'extension' : 'phone number';
      const newType = isExtension(trimmedValue) ? 'extension' : 'phone number';
      Toast.error(`You cannot mix ${existingType}s with ${newType}s. Please use only one type.`);
      setInputValue('');
      return;
    }
    
    setRecipients([...recipients, trimmedValue]);
    setInputValue('');
    setShowDropdown(false);
  };

  const removeRecipient = (recipient) => {
    setRecipients(recipients.filter(r => r !== recipient));
  };

  const selectContact = (contact) => {
    if (recipients.includes(contact.number)) return;

    if (isSingleRecipientOnly && recipients.length >= 1) {
      Toast.error('Only one recipient is allowed.');
      setInputValue('');
      setShowDropdown(false);
      return;
    }
    
    // Check if we can add this recipient type
    if (!canAddRecipient(contact.number)) {
      const existingType = isExtension(recipients[0]) ? 'extension' : 'phone number';
      const newType = isExtension(contact.number) ? 'extension' : 'phone number';
      Toast.error(`You cannot mix ${existingType}s with ${newType}s. Please use only one type.`);
      setInputValue('');
      setShowDropdown(false);
      return;
    }
    
    setRecipients([...recipients, contact.number]);
    setInputValue('');
    setShowDropdown(false);
    inputRef.current.focus();
  };

  const getDisplayValue = () => {
    if (!selectedDid) return '';
    if (selectedDid.number) return selectedDid.number;
    return `${selectedDid.did}${selectedDid.primary_user ? ` - Shared By: ${selectedDid.primary_user}` : ''}`;
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.did-dropdown-container')) {
        setShowDidDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex sm:items-start sm:justify-center bg-black bg-opacity-50 sm:pt-[60px]">
      <div className={`
        bg-white w-full flex flex-col
        sm:rounded-lg sm:shadow-xl sm:max-w-md sm:mx-4 sm:h-[86vh]
        
        /* Mobile styles */
        fixed bottom-0 left-0 right-0
        h-[100vh] rounded-t-[20px]
        transform transition-transform duration-300
        ${isOpen ? 'translate-y-0' : 'translate-y-full'}
        
        /* Desktop styles */
        sm:static sm:transform-none sm:rounded-t-lg
      `}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 md:py-4 border-b border-gray-200">
          {/* Mobile handle for visual indication of draggability */}
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-12 h-1.5 bg-gray-300 rounded-full sm:hidden" />
          
          <button 
            onClick={()=> {
              onClose();
              // Remove new_message and to_user from URL
              const url = new URL(window.location.href);
              url.searchParams.delete('new_message');
              url.searchParams.delete('to_user');
              window.history.replaceState({}, '', url.toString());
            }}
            className="text-gray-600 hover:text-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h3 className="text-lg font-semibold text-gray-800">New Message</h3>
          <div className="w-6"></div>
        </div>

        {/* Content area - make it scrollable */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* To Field */}
          <div className="px-4 py-3 md:py-4 border-b border-gray-200">
            <div className="relative">
              {/* From Field */}
              {dids?.length > 0 && (
                <div className="flex items-center gap-2 mb-3 did-dropdown-container relative">
                  <div className="flex-grow relative">
                    {areAllRecipientsInternal(recipients) ? (
                      <div className="w-full bg-blue-50 text-blue-700 border border-blue-200 rounded-lg px-3 py-2">
                        <span className="font-medium">Internal Chat</span>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          readOnly
                          value={getDisplayValue()}
                          onClick={() => setShowDidDropdown(!showDidDropdown)}
                          className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800 cursor-pointer pr-10"
                        />
                        <FontAwesomeIcon 
                          icon={faChevronDown}
                          className={`absolute right-3 top-3 text-gray-400 transition-transform duration-200 ${showDidDropdown ? 'transform rotate-180' : ''}`}
                        />
                      </>
                    )}
                  </div>

                  {!areAllRecipientsInternal(recipients) && showDidDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                      {/* My Numbers Section */}
                      {dids.some(did => did.number) && (
                        <>
                          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                            <span className="text-sm font-bold text-gray-700">My Numbers</span>
                          </div>
                          {dids.filter(did => did.number).map((did, idx) => (
                            <div
                              key={`own-${idx}`}
                              onClick={() => {
                                setSelectedDid(did);
                                setShowDidDropdown(false);
                              }}
                              className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                                (did.number === selectedDid?.number) ? 'bg-blue-50' : ''
                              }`}
                            >
                              <span className="text-gray-800">{did.number}</span>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Shared DIDs Section */}
                      {dids.some(did => did.did) && (
                        <>
                          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                            <span className="text-sm font-bold text-gray-700">Shared DIDs</span>
                          </div>
                          {dids.filter(did => did.did).map((did, idx) => (
                            <div
                              key={`shared-${idx}`}
                              onClick={() => {
                                setSelectedDid(did);
                                setShowDidDropdown(false);
                              }}
                              className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                                (did.did === selectedDid?.did) ? 'bg-blue-50' : ''
                              }`}
                            >
                              <span className="text-gray-800">
                                {did.did}
                                {did.primary_user && (
                                  <span className="text-gray-500 text-sm ml-2">
                                    - Shared By: {did.primary_user}
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 rounded-lg min-h-[40px]">
                <label className="text-gray-700 font-medium mb-0">To:</label>
                
                {/* Recipient chips */}
                {recipients.map((recipient, index) => (
                  <div 
                    key={index} 
                    className="flex items-center bg-blue-100 text-blue-800 rounded-full px-3 py-1"
                  >
                    <span className="text-sm mr-1">{recipient}</span>
                    <button 
                      onClick={() => removeRecipient(recipient)} 
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                
                {/* Input field — hidden for INTERACTIVETELVIP once one recipient is set */}
                {!(isSingleRecipientOnly && recipients.length >= 1) && (
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={handleInputKeyDown}
                    onBlur={() => {
                      if (inputValue.trim()) {
                        addRecipient(inputValue);
                      }
                    }}
                    className="flex-grow min-w-[100px] bg-transparent border-none focus:outline-none focus:ring-0 text-gray-800"
                    placeholder={recipients.length === 0 ? "Enter name or number" : ""}
                    enterKeyHint="enter"
                    autoComplete="off"
                  />
                )}
              </div>
              
              {/* Contacts dropdown */}
              {showDropdown && filteredContacts.length > 0 && !(isSingleRecipientOnly && recipients.length >= 1) && (
                <div 
                  ref={dropdownRef}
                  className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
                >
                  {filteredContacts.map(contact => (
                    <div
                      key={contact.id}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur from firing before click
                        selectContact(contact);
                      }}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                    >
                      <div className="font-medium text-gray-800">{contact.name}</div>
                      <div className="text-sm text-gray-500">{contact.number}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Message Area - make it scrollable */}
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            {/* File Preview */}
            {filePreview && (
              <div className="mb-4 p-2 bg-white rounded-lg shadow-lg max-w-[300px] relative">
                {fileType === 'image' && (
                  <img 
                    src={filePreview} 
                    alt="Preview" 
                    className="max-h-[200px] rounded-lg cursor-pointer" 
                  />
                )}
                {fileType === 'video' && (
                  <video 
                    src={filePreview} 
                    controls 
                    className="max-h-[200px] rounded-lg"
                  />
                )}
                {fileType === 'audio' && (
                  <audio src={filePreview} controls className="w-[280px]" />
                )}
                {fileType === 'application' && (
                  <div className="flex items-center p-2 bg-gray-100 rounded-lg">
                    <FontAwesomeIcon icon={faFile} className="w-6 h-6 mr-2 text-gray-600" />
                    <span className="text-sm text-gray-700 truncate">{selectedFile.name}</span>
                  </div>
                )}
                <button
                  onClick={clearFilePreview}
                  className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-gray-800 bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Message Input - Keep it fixed at the bottom */}
          <div className="px-4 py-3 md:py-4 bg-white border-t border-gray-200">
            <div className="flex items-center space-x-2">
              {/* Attachment Button */}
              <div className="relative">
                <button
                  onClick={() => setShowMediaOptions(!showMediaOptions)}
                  style={(() => {
                    const styles = localStorage.getItem("messaging_styles");
                    let buttonStyles = {};
                    
                    if (styles) {
                      try {
                        const parsedStyles = JSON.parse(styles);
                        buttonStyles = {
                          backgroundColor: parsedStyles.message_buttons_color || '#3B82F6',
                          color: parsedStyles.message_buttons_font_color || '#FFFFFF'
                        };
                      } catch (e) {
                        console.error('Error parsing messaging styles:', e);
                      }
                    }
                    return buttonStyles;
                  })()}
                  className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200
                    ${!localStorage.getItem("messaging_styles") ? 'bg-blue-500 text-white hover:bg-blue-600' : 
                    'hover:opacity-80'}`}
                >
                  <FontAwesomeIcon icon={faPaperclip} className="w-5 h-5" />
                </button>

                {/* Media Options Popup */}
                {showMediaOptions && (
                  <div className="absolute bottom-12 left-0 bg-white rounded-lg shadow-lg p-2 w-48">
                    {/* Photo/Video Input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                      accept="image/*,video/*"
                className="hidden"
                      id="photo-video-input"
                    />
                    {/* Document Input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                      className="hidden"
                      id="document-input"
                    />
                    {/* Audio Input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="audio/*"
                      className="hidden"
                      id="audio-input"
              />

              <button
                      onClick={() => document.getElementById('photo-video-input').click()}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                    >
                      <FontAwesomeIcon icon={faImage} className="w-4 h-4 mr-2" />
                      Photo/Video
                    </button>
                    <button
                      onClick={() => document.getElementById('document-input').click()}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                    >
                      <FontAwesomeIcon icon={faFile} className="w-4 h-4 mr-2" />
                      Document
                    </button>
                    <button
                      onClick={() => document.getElementById('audio-input').click()}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
              >
                      <FontAwesomeIcon icon={faMicrophone} className="w-4 h-4 mr-2" />
                      Audio
              </button>
                  </div>
                )}
              </div>

              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={handleMessageChange}
                  onKeyDown={handleKeyPress}
                  placeholder={filePreview ? "Add a caption..." : "Type a message..."}
                  className={`
                    w-full min-h-[48px] max-h-[120px] px-4 py-2 bg-gray-100 text-base pr-12
                    ${isMultiLine ? 'rounded-xl' : 'rounded-full'}
                    resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 
                    overflow-y-auto transition-all duration-200
                  `}
                  rows={1}
                />
                
                {/* Emoji Button */}
                <div className="absolute right-0 inset-y-0 flex items-center pr-2 pointer-events-none">
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="text-gray-500 hover:text-gray-700 pointer-events-auto"
                  >
                     <FontAwesomeIcon 
                      icon={showEmojiPicker ? faTimes : faSmile} 
                      style={{ fontSize: '24px' }} 
                    />
                  </button>
                </div>

                {/* Emoji Picker */}
                {showEmojiPicker && (
                  <div className="absolute bottom-full right-0 mb-2">
                    <div className="bg-white rounded-lg shadow-lg">
                      <EmojiPicker
                        onEmojiClick={handleEmojiClick}
                        width={300}
                        height={400}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Send Button */}
              <button
                onClick={handleSendMessage}
                disabled={(!message.trim() && !selectedFile) || recipients.length === 0}
                style={(() => {
                  const isDisabled = (!message.trim() && !selectedFile) || recipients.length === 0;
                  if (isDisabled) {
                    return {
                      backgroundColor: '#D1D5DB',
                      color: '#6B7280',
                      cursor: 'not-allowed'
                    };
                  }
                  
                  const styles = localStorage.getItem("messaging_styles");
                  let buttonStyles = {
                    backgroundColor: '#3B82F6',
                    color: '#FFFFFF'
                  };
                  
                  if (styles) {
                    try {
                      const parsedStyles = JSON.parse(styles);
                      buttonStyles = {
                        backgroundColor: parsedStyles.message_buttons_color || '#3B82F6',
                        color: parsedStyles.message_buttons_font_color || '#FFFFFF'
                      };
                    } catch (e) {
                      console.error('Error parsing messaging styles:', e);
                    }
                  }
                  return buttonStyles;
                })()}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200
                  ${(!message.trim() && !selectedFile) || recipients.length === 0 
                    ? '' 
                    : 'hover:opacity-80'}`}
              >
                {sending ? (
                  <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewMessageModal; 